// src/components/Navbar.js
import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu, X, ChevronDown, Satellite, Globe2,
  Database, BookOpen, Users, BarChart2,
  AlertTriangle, Cpu, GraduationCap, Layers,
  ExternalLink, Home, Radio, MapPin
} from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../context/ThemeContext";
import { API_URLS } from "../config";

/* ─── Navigation structure ─────────────────────────────────── */
const WEATHER_URL = API_URLS.weatherApp;
const SPACE_URL   = API_URLS.spaceApp;

const NAV_ITEMS = [
  {
    label: "Home",
    to: "/",
    icon: Home,
  },
  {
    label: "Monitoring",
    icon: Satellite,
    children: [
      { label: "TEC Dashboard",         to: "/products/dashboardtec",  icon: BarChart2,   desc: "Total Electron Content monitor" },
      { label: "Scintillation Monitor",  to: "/products/scintillation", icon: Radio,       desc: "GPS signal scintillation tracking" },
      { label: "Weather Forecasting",    href: WEATHER_URL,             icon: Globe2,      desc: "Hourly and Daily weather forecasts up to 7 days" },
      { label: "Space Weather Products", href: SPACE_URL,               icon: Cpu,         desc: "Advanced space weather model outputs" },
    ],
  },
  {
    label: "Alerts",
    to: "/alerts",
    icon: AlertTriangle,
    highlight: true,
  },
  {
    label: "Data Portal",
    to: "/data",
    icon: Database,
    children: [
      { label: "Download Data",        to: "/data",      icon: Database,  desc: "TEC, GNSS, ionospheric datasets" },
      { label: "Community Dashboards", to: "/community", icon: Users,     desc: "Sector-specific alert views" },
      { label: "API Documentation",    to: "/data#api",  icon: Layers,    desc: "RESTful API reference & examples" },
    ],
  },
  {
    label: "Research",
    icon: BookOpen,
    children: [
      { label: "Publications",  to: "/publications", icon: BookOpen,       desc: "Peer-reviewed papers & reports" },
      { label: "Partners",      to: "/partners",     icon: Users,          desc: "National & international collaborators" },
      { label: "Education",     to: "/education",    icon: GraduationCap,  desc: "Space weather learning resources" },
    ],
  },
  {
    label: "More",
    icon: Layers,
    children: [
      { label: "Impact & Benefits", to: "/benefits",  icon: Globe2,  desc: "Reach societal impacts" },
      { label: "Station Map",       to: "/map",       icon: MapPin,  desc: "GNSS and sensor network map" },
      { label: "Resources",         to: "/resources", icon: Layers,  desc: "Tools, links and reference docs" },
    ],
  },
];

/** Fetch live disaster counts for the Alerts badge in the navbar */
function useDisasterCounts() {
  const [counts, setCounts] = useState({ total: 0, critical: 0 });

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        const [wRes, sRes] = await Promise.allSettled([
          fetch(API_URLS.weatherDisasters, { signal: AbortSignal.timeout(6_000), cache: 'no-store' })
            .then((r) => r.ok ? r.json() : [])
            .then((d) => Array.isArray(d) ? d : (d?.data ?? [])),
          fetch(API_URLS.spaceDisasters, { signal: AbortSignal.timeout(6_000), cache: 'no-store' })
            .then((r) => r.ok ? r.json() : [])
            .then((d) => Array.isArray(d) ? d : (d?.data ?? [])),
        ]);
        const all = [
          ...(wRes.status === 'fulfilled' ? wRes.value : []),
          ...(sRes.status === 'fulfilled' ? sRes.value : []),
        ];
        if (alive) setCounts({
          total:    all.length,
          critical: all.filter((d) => d.severity === 'critical').length,
        });
      } catch { /* silently ignore */ }
    }

    load();
    const id = setInterval(load, 120_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return counts;
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const { total: alertTotal, critical: alertCritical } = useDisasterCounts();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const location = useLocation();
  const navRef = useRef(null);
  const drawerRef = useRef(null);
  const hamburgerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Focus trap inside mobile drawer + Escape to close
  const handleDrawerKeyDown = useCallback((e) => {
    if (!mobileOpen) return;
    if (e.key === "Escape") {
      setMobileOpen(false);
      hamburgerRef.current?.focus();
      return;
    }
    if (e.key !== "Tab") return;
    const drawer = drawerRef.current;
    if (!drawer) return;
    const focusable = drawer.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, [mobileOpen]);

  // Close mobile on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const isActive = (to) => to && location.pathname === to;

  return (
    <nav
      ref={navRef}
      className="sticky top-0 z-40"
      style={{
        background:   'var(--bg-void)',
        borderBottom: '1px solid var(--border-subtle)',
        borderLeft:   '4px solid #e2bc12',   /* pinned brand gold — always vivid, never dims in light mode */
        boxShadow:    isDark
          ? '0 2px 16px var(--shadow)'
          : '0 2px 12px rgba(0,0,0,0.09), 0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {/* ── Single unified bar ─────────────────────────────── */}
      <div style={{ padding: '0 64px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '56px' }}>
          {/* ── Left: logos + brand ──────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <Link to="/" style={{ flexShrink: 0, lineHeight: 0 }}>
              <img src="/BU.png" alt="Busitema University"
                style={{ height: '30px', width: 'auto', objectFit: 'contain',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.35))' }} />
            </Link>
            <Link to="/" style={{ flexShrink: 0, lineHeight: 0 }}>
              <img src="/STI-Logo-White.png" alt="SWT"
                style={{ height: '30px', width: 'auto', objectFit: 'contain',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.35))' }} />
            </Link>
            <Link to="/" style={{ flexShrink: 0, lineHeight: 0 }}>
              <img src="/swt_logo.png" alt="SWT"
                style={{ height: '30px', width: 'auto', objectFit: 'contain',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.35))' }} />
            </Link>
            <div className="hidden lg:flex flex-col"
              style={{ paddingLeft: '8px', borderLeft: '1px solid var(--border-subtle)', lineHeight: 1.3 }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 800,
                color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                Space Weather Based Early Warning Tool
              </span>
              <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                Busitema University · Uganda
              </span>
            </div>
          </div>

          {/* ── Center: desktop nav ───────────────────────── */}
          <div className="hidden lg:flex items-center" style={{ gap: 0, flex: '0 1 auto', minWidth: 0 }}>
            {NAV_ITEMS.map((item) => (
              <NavItemDesktop
                key={item.label}
                item={item}
                isActive={isActive}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
              />
            ))}
          </div>

          {/* ── Right: live disaster counter + theme toggle ──── */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0" style={{ marginLeft: 'auto' }}>
            <Link to="/alerts" style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              fontSize: 'var(--text-xs)', fontWeight: 600,
              color: alertCritical > 0 ? '#ef4444' : alertTotal > 0 ? '#f97316' : 'var(--status-safe)',
              textDecoration: 'none',
              padding: '4px 10px', borderRadius: 'var(--radius-sm)',
              background: alertCritical > 0 ? 'rgba(239,68,68,0.12)' : alertTotal > 0 ? 'rgba(249,115,22,0.1)' : 'var(--status-safe-bg)',
              border: `1px solid ${alertCritical > 0 ? '#ef4444' : alertTotal > 0 ? '#f97316' : 'var(--status-safe)'}`,
              whiteSpace: 'nowrap',
              transition: 'all 0.3s',
            }}>
              <span
                className="pulse-dot"
                style={{
                  width: '6px', height: '6px',
                  background: alertCritical > 0 ? '#ef4444' : alertTotal > 0 ? '#f97316' : 'var(--status-safe)',
                }}
              />
              {alertCritical > 0
                ? `${alertCritical} Critical Alert${alertCritical > 1 ? 's' : ''}`
                : alertTotal > 0
                  ? `${alertTotal} Active Alert${alertTotal > 1 ? 's' : ''}`
                  : 'Live Alerts'
              }
            </Link>
            <ThemeToggle compact />
          </div>

          {/* ── Mobile: hamburger ─────────────────────────── */}
          <button
            ref={hamburgerRef}
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden ml-auto p-2 rounded transition-colors"
            style={{ color: 'var(--text-primary)' }}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-drawer"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* ── Mobile drawer ─────────────────────────────────── */}
      {mobileOpen && (
        <div
          id="mobile-drawer"
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          className="lg:hidden overflow-y-auto"
          style={{
            background: 'var(--bg-void)',
            borderTop: '1px solid var(--border-subtle)',
            maxHeight: '80vh',
          }}
          onKeyDown={handleDrawerKeyDown}
        >
          <div className="py-2">
            {NAV_ITEMS.map((item) => (
              <MobileNavItem
                key={item.label}
                item={item}
                isActive={isActive}
                expanded={mobileExpanded === item.label}
                onToggle={(label) =>
                  setMobileExpanded((prev) => (prev === label ? null : label))
                }
              />
            ))}

            {/* Mobile quick actions */}
            <div className="px-4 py-3 flex flex-col gap-2 mt-2" style={{ borderTop: "1px solid var(--border-subtle)" }}>
              <div className="flex justify-center pb-1">
                <ThemeToggle />
              </div>
              <a
                href={WEATHER_URL}
                className="bu-btn bu-btn-secondary flex items-center justify-center gap-2"
                style={{ textDecoration: "none", width: "100%" }}
              >
                <Globe2 size={16} /> Weather App
              </a>
              <a
                href={SPACE_URL}
                className="bu-btn bu-btn-ghost flex items-center justify-center gap-2"
                style={{
                  textDecoration: "none",
                  width:          "100%",
                  border:         "1px solid var(--border-medium)",
                }}
              >
                <Cpu size={16} /> Space Weather
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

/* ─── Desktop Nav Item ──────────────────────────────────────── */
function NavItemDesktop({ item, isActive, openDropdown, setOpenDropdown }) {
  const hasChildren = item.children && item.children.length > 0;
  const active = isActive(item.to);
  const dropOpen = openDropdown === item.label;

  const accentColor  = "var(--text-accent)";
  const defaultColor = "var(--text-secondary)";
  const highlightColor = "#ff9800";  /* semantic alert orange — same in both themes */

  const baseStyle = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "0 0.45rem",
    minHeight: "56px",
    fontSize: "0.78rem",
    fontWeight: 600,
    textDecoration: "none",
    cursor: "pointer",
    transition: "color 0.2s",
    whiteSpace: "nowrap",
    position: "relative",
    borderBottom: active ? `3px solid ${accentColor}` : "3px solid transparent",
    color: active ? accentColor : item.highlight ? highlightColor : defaultColor,
    background: "transparent",
    border: "none",
    outline: "none",
  };

  if (!hasChildren) {
    if (item.href) {
      return (
        <a href={item.href} style={baseStyle}>
          {item.icon && <item.icon size={15} />}
          {item.label}
        </a>
      );
    }
    return (
      <Link
        to={item.to}
        style={{
          ...baseStyle,
          borderBottom: active ? `3px solid ${accentColor}` : "3px solid transparent",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = accentColor; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = active ? accentColor : item.highlight ? highlightColor : defaultColor; }}
      >
        {item.icon && <item.icon size={15} />}
        {item.label}
        {item.highlight && (
          <span
            className="pulse-dot ml-0.5"
            style={{ width: '7px', height: '7px', background: 'var(--status-warning)' }}
          />
        )}
      </Link>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpenDropdown(item.label)}
      onMouseLeave={() => setOpenDropdown(null)}
    >
      <button
        style={baseStyle}
        onMouseEnter={(e) => { e.currentTarget.style.color = accentColor; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = defaultColor; }}
      >
        {item.icon && <item.icon size={15} />}
        {item.label}
        <ChevronDown
          size={13}
          style={{
            transform: dropOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </button>

      {/* Dropdown */}
      <div
        style={{
          position: "absolute",
          top: "100%",
          left: 0,
          minWidth: "260px",
          background: "var(--bg-card)",
          border: "1px solid var(--border-medium)",
          borderRadius: "8px",
          boxShadow: "0 8px 32px var(--shadow)",
          opacity: dropOpen ? 1 : 0,
          visibility: dropOpen ? "visible" : "hidden",
          transform: dropOpen ? "translateY(0)" : "translateY(-6px)",
          transition: "all 0.18s ease",
          zIndex: 100,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "0.5rem 0" }}>
          {item.children.map((child) => (
            <DropdownLink key={child.label} child={child} />
          ))}
        </div>
      </div>
    </div>
  );
}

function DropdownLink({ child }) {
  const [hovered, setHovered] = useState(false);
  const content = (
    <>
      <div
        style={{
          width:      "32px",
          height:     "32px",
          borderRadius: "8px",
          background: hovered ? "var(--accent-dim)" : "var(--bg-elevated)",
          display:    "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.2s",
          flexShrink: 0,
        }}
      >
        {child.icon && (
          <child.icon
            size={15}
            color={hovered ? "var(--text-accent)" : "var(--text-muted)"}
          />
        )}
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: "0.87rem", color: hovered ? "var(--text-accent)" : "var(--text-primary)" }}>
          {child.label}
          
        </div>
        {child.desc && (
          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "2px" }}>
            {child.desc}
          </div>
        )}
      </div>
    </>
  );

  const sharedStyle = {
    display:       "flex",
    alignItems:    "center",
    gap:           "12px",
    padding:       "0.65rem 1rem",
    textDecoration:"none",
    background:    hovered ? "var(--accent-dim)" : "transparent",
    borderLeft:    hovered ? "3px solid var(--accent)" : "3px solid transparent",
    transition:    "all 0.15s",
    cursor:        "pointer",
  };

  if (child.href) {
    return (
      <a
        href={child.href}
        style={sharedStyle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={child.to}
      style={sharedStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {content}
    </Link>
  );
}

/* ─── Mobile Nav Item ───────────────────────────────────────── */
function MobileNavItem({ item, isActive, expanded, onToggle }) {
  const hasChildren = item.children && item.children.length > 0;
  const active = isActive(item.to);

  const baseCls = `flex items-center gap-3 w-full px-4 py-3 text-sm font-semibold transition-colors`;
  const accentColor  = "var(--text-accent)";
  const defaultColor = "var(--text-secondary)";

  if (!hasChildren) {
    const inner = (
      <>
        {item.icon && <item.icon size={17} />}
        <span>{item.label}</span>
        {item.highlight && (
          <span
            className="pulse-dot"
            style={{ width: '8px', height: '8px', background: 'var(--status-warning)' }}
          />
        )}
      </>
    );

    if (item.href) {
      return (
        <a
          href={item.href}
          className={baseCls}
          style={{ color: active ? accentColor : defaultColor, textDecoration: "none" }}
        >
          {inner}
          <ExternalLink size={13} className="ml-auto opacity-50" />
        </a>
      );
    }

    return (
      <Link
        to={item.to}
        className={baseCls}
        style={{ color: active ? accentColor : defaultColor, textDecoration: "none" }}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => onToggle(item.label)}
        className={baseCls}
        style={{
          color:     expanded ? accentColor : defaultColor,
          background:expanded ? "var(--accent-dim)" : "transparent",
          width:     "100%",
          textAlign: "left",
        }}
      >
        {item.icon && <item.icon size={17} />}
        <span>{item.label}</span>
        <ChevronDown
          size={15}
          className="ml-auto"
          style={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </button>

      {expanded && (
        <div
          style={{
            background: "var(--bg-elevated)",
            borderLeft: "3px solid var(--accent)",
            marginLeft: "1rem",
          }}
        >
          {item.children.map((child) => {
            const inner = (
              <>
                {child.icon && <child.icon size={14} />}
                <div>
                  <div style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)" }}>
                    {child.label}
                  </div>
                  {child.desc && (
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                      {child.desc}
                    </div>
                  )}
                </div>
                
              </>
            );

            if (child.href) {
              return (
                <a
                  key={child.label}
                  href={child.href}
                  className="flex items-center gap-3 px-4 py-2.5 transition-colors"
                  style={{ textDecoration: "none", color: "var(--text-secondary)" }}
                >
                  {inner}
                </a>
              );
            }

            return (
              <Link
                key={child.label}
                to={child.to}
                className="flex items-center gap-3 px-4 py-2.5 transition-colors"
                style={{ textDecoration: "none", color: "var(--text-secondary)" }}
              >
                {inner}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
